import {fetchDownloadFile, fetchResetRequest} from "@/api/user/home";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {useMutation, useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import {PulseLoader} from "react-spinners";
import {toast} from "react-toastify";

type InformationReviewProps = {
    status: string;
    contentDetail: React.ReactNode;
    setStartUpload?: Dispatch<SetStateAction<boolean>>;
};

/**
 * InformationReview
 *
 * کامپوننتی برای نمایش نتیجه بررسی اعتبار کاربر و ارائه گزینه‌های اقدام بعدی.
 * - نمایش وضعیت بررسی ("موفق" یا "ناموفق") همراه با جزئیات.
 * - امکان دانلود فایل اعتبارسنجی در صورت موفقیت.
 * - امکان بازگشت به مرحله اول یا ورود به فرم ارائه مدارک.
 *
 * @param {Object} props
 * @param {string} props.status وضعیت بررسی اعتبار ("موفق" یا "ناموفق")
 * @param {React.ReactNode} props.contentDetail محتوای توضیحی برای کاربر
 * @param {Dispatch<SetStateAction<boolean>>} [props.setStartUpload] تابع برای شروع فرآیند بارگذاری مدارک
 *
 * @component
 * @returns JSX.Element
 */

const InformationReview = (props: InformationReviewProps) => {
    const {status, contentDetail, setStartUpload} = props;
    const [isStatusSuccess, setIsStatusSuccess] = useState<boolean>(false);
    const [enabledReset, setEnabledReset] = useState(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    // Use fetchDownloadFile mutation
    const {mutate: fetchRequestDownload, isPending: isPendingDownload} = useMutation({
        mutationFn: fetchDownloadFile,
        onSuccess: (data: any) => {
            setIsDownloading(false);
            // Handle successful download
            handleDownloadResponse(data);
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setIsDownloading(false);
            toast.error(error?.response?.data?.message || "خطا در دانلود فایل");
        },
    });

    const {data: dataResetRequest, isPending: isPendingResetRequest, isSuccess} = useQuery({
        queryKey: ["ResetRequest-api"],
        queryFn: fetchResetRequest,
        enabled: enabledReset,
    });

    const handleDownloadResponse = async (response: any) => {
        try {
            // Check if response is a Blob (binary file)
            if (response instanceof Blob) {
                // Read first few bytes to check if it's a PDF
                const reader = new FileReader();
                const firstBytes = await new Promise<string>((resolve) => {
                    reader.onload = () => resolve(reader.result as string);
                    reader.readAsText(response.slice(0, 10));
                });

                // If it starts with %PDF, it's a PDF file
                if (firstBytes.includes('%PDF')) {
                    // Download as PDF
                    const fileURL = URL.createObjectURL(response);
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = 'validation_result.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                    toast.success("فایل PDF با موفقیت دانلود شد");
                    return;
                }

                // If not PDF, read as text
                const fileContent = await response.text();

                // Try to parse as JSON
                try {
                    const jsonData = JSON.parse(fileContent);
                    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
                        type: 'text/plain; charset=utf-8'
                    });
                    const fileURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = 'validation_result.txt';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                    toast.success("فایل با موفقیت دانلود شد");
                } catch {
                    // If not JSON, save as plain text
                    const blob = new Blob([fileContent], {
                        type: 'text/plain; charset=utf-8'
                    });
                    const fileURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = 'validation_result.txt';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                    toast.success("فایل با موفقیت دانلود شد");
                }
            }
            // If response is a string
            else if (typeof response === 'string') {
                try {
                    // Try to parse as JSON
                    const jsonData = JSON.parse(response);
                    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
                        type: 'text/plain; charset=utf-8'
                    });
                    const fileURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = 'validation_result.txt';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                    toast.success("فایل با موفقیت دانلود شد");
                } catch {
                    // If not JSON, save as plain text
                    const blob = new Blob([response], {
                        type: 'text/plain; charset=utf-8'
                    });
                    const fileURL = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.download = 'validation_result.txt';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(fileURL);
                    toast.success("فایل با موفقیت دانلود شد");
                }
            }
            // If response is an object
            else {
                const blob = new Blob([JSON.stringify(response, null, 2)], {
                    type: 'text/plain; charset=utf-8'
                });
                const fileURL = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = 'validation_result.txt';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(fileURL);
                toast.success("فایل با موفقیت دانلود شد");
            }
        } catch (error) {
            console.error('Download error:', error);
            toast.error("خطا در دانلود فایل");
        }
    };

    const handleClickDownload = () => {
        setIsDownloading(true);
        fetchRequestDownload();
    };

    useEffect(() => {
        if (status === "موفق") {
            setIsStatusSuccess(true);
        }
        if (isSuccess) {
            window.location.reload();
        }
    }, [status, isSuccess]);

    const handleClickAction = () => {
        if (status === "ناموفق") {
            setEnabledReset(true);
        }
        setStartUpload && setStartUpload(true);
    };

    return (
        <StackKit direction="column" sx={{
            width: "100%",
            justifyContent: "space-around",
            height: "486px",
            marginBottom: "30px",
            padding: "10px 30px",
            alignItems: "start"
        }}>
            <StackKit direction={"column"} gap={3}>
                <StackKit direction={"row"}>
                    <TypoGraphyKit variant="h6">نتیجه بررسی اطلاعات:</TypoGraphyKit>
                    <TypoGraphyKit variant="h6" sx={{color: isStatusSuccess ? "#009951" : "#C00F0C"}}>
                        {status}
                    </TypoGraphyKit>
                </StackKit>
                <StackKit alignItems={"start"}>{contentDetail}</StackKit>
            </StackKit>
            <StackKit direction={"row"} justifyContent={"space-between"} sx={{width: "100%"}}>
                <CustomButton
                    onClick={handleClickDownload}
                    disabled={isPendingDownload || isDownloading}
                >
                    {isPendingDownload || isDownloading ? (
                        <StackKit sx={{padding: "5px"}}>
                            <PulseLoader color="#fff" size={5} speedMultiplier={1}/>
                        </StackKit>
                    ) : (
                        "دانلود فایل اعتبار سنجی"
                    )}
                </CustomButton>
                <CustomButton onClick={handleClickAction}>
                    {isStatusSuccess ? "ورود به بخش ارائه مدارک" : "بازگشت به مرحله اول"}
                </CustomButton>
            </StackKit>
        </StackKit>
    );
};

export default InformationReview;